import React, { useEffect } from 'react';

import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { useCookies } from 'react-cookie';
import 'react-toastify/dist/ReactToastify.css';

import { Layout } from './components/Layout/Layout';
import { router } from './router/router';
import { SessionProvider, useSession } from './context/SessionContext';
import { canAccessRoute } from './utils/permissions';
import axiosInstance from './utils/axiosInstance';

function ApplicationRoutes() {
  const [cookies, , removeCookie] = useCookies(['invoice_csrf']);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: sessionLoading } = useSession();

  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/login');
    }
  }, [location.pathname, navigate, cookies.invoice_csrf]);

  useEffect(() => {
    let timeoutId;
    const INACTIVE_TIMEOUT = 10 * 60 * 1000;

    const handleLogout = async () => {
      try { await axiosInstance('/user/logout', 'post', {}); } catch (_error) { /* Session still clears locally. */ }
      removeCookie('invoice_csrf', { path: '/' });
      navigate('/login');
    };

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(handleLogout, INACTIVE_TIMEOUT);
    };

    if (cookies.invoice_csrf) {
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      events.forEach((event) => {
        document.addEventListener(event, resetTimer);
      });

      resetTimer();

      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        events.forEach((event) => {
          document.removeEventListener(event, resetTimer);
        });
      };
    }
  }, [cookies.invoice_csrf, navigate, removeCookie]);

  if (cookies.invoice_csrf && sessionLoading) {
    return <div className='min-h-screen bg-[var(--surface-page)]' aria-busy='true' />;
  }

  return (
    <div className='App'>
      <Layout>
        <Routes>
          {router.map((route) => {
            if (cookies.invoice_csrf) {
              if (user?.mustChangePassword && route.path !== '/change-password') {
                return <Route key={route.path} path={route.path} element={<Navigate to='/change-password' replace />} />;
              }
              if (!user?.mustChangePassword && route.path === '/change-password') {
                return <Route key={route.path} path={route.path} element={<Navigate to='/dashboard' replace />} />;
              }
              if (route.path === '/login' || route.path === '/two-factor') {
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={<Navigate to='/dashboard' replace />}
                  />
                );
              }
              if (!canAccessRoute(user?.role, route.roles)) {
                return <Route key={route.path} path={route.path} element={<Navigate to='/dashboard' replace />} />;
              }
              return <Route key={route.path} path={route.path} element={route.element} />;
            } else {
              if (route.path !== '/login' && route.path !== '/two-factor' && !route.public) {
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={<Navigate to='/login' replace />}
                  />
                );
              }
              return <Route key={route.path} path={route.path} element={route.element} />;
            }
          })}
          <Route path='*' element={<Navigate to={cookies.invoice_csrf ? '/dashboard' : '/login'} replace />} />
        </Routes>
      </Layout>
    </div>
  );
}

function App() {
  return <SessionProvider><ApplicationRoutes /></SessionProvider>;
}

export default App;
